import React, { Component } from "react";
import { Alert } from "reactstrap";
import { connect } from "react-redux";
import { BrowserRouter as Router, Route, Switch, Redirect } from "react-router-dom";
import { IntlProvider } from "react-intl";
import { Detector } from "react-detect-offline";
import "./helpers/Firebase";
import Amplify, { Hub, I18n } from "aws-amplify";
import * as log from "loglevel";
import IntlMessages from "./helpers/IntlMessages";
import AppLocale from "./lang";
import ColorSwitcher from "./components/common/ColorSwitcher";
import NotificationContainer from "./components/common/react-notifications/NotificationContainer";
import { isMultiColorActive } from "./constants/defaultValues";
import main from "./views";
import app from "./views/app";
import errorView from "./views/error";
import { getDirection } from "./helpers/Utils";
import dict from "./constants/dict";
import { setRequestShowToast, setRequestLoading } from "./redux/request/actions";
import { setBookingShowToast } from "./redux/booking/actions";
import {
  internetConnectedStatusChanged,
  acquirePolicies,
  iotConnectCallbackStart,
  iotCloseCallbackStart,
  clearSubscribedTopics,
  setMessageHandlerStatus,
  setDeviceStatus,
  triggerOnNewMessage,
  clearPolicies
} from "./redux/iot/actions";
import { webPushPermissionStart, appPushTokenSuccess } from "./redux/auth/actions";
import Dictionary from "./lang/entries/Dictionary";
import { sendMessageToWebview } from "./helpers/webview";
import VersionChecker from "./components/ErrorBoundry/versionChecker";

console.log("process.env.NODE_ENV: ", process.env.NODE_ENV)
if (process.env.NODE_ENV === 'development') {
  Amplify.Logger.LOG_LEVEL = "INFO";
  log.setLevel("INFO");
} else {
  Amplify.Logger.LOG_LEVEL = "INFO";
  log.setLevel("INFO");
}
I18n.putVocabularies(dict);

let policyRetryCount = 0;
let webPushPermissionRetryCount = 0;
const MAX_POLICY_RETRY_COUNT = 2;
const MAX_WEBPUSH_PERMISSION_RETRY_COUNT = 2;
class DebugRouter extends Router {
  constructor(props) {
    super(props);
  }
}

class App extends Component {
  constructor(props) {
    super(props);
    this.state = {
      reconnectTimer: 0,
      thirtySecondsPassedAfterDidMount: false
    };
    this.iotHubListener = data => {
      const { payload } = data;
      this.onIotEvent(payload);
    };
    Hub.listen("IOTChannel", this.iotHubListener); // Add this component as a listener of auth events.
  }

  componentWillMount() {
    const direction = getDirection();
    if (direction.isRtl) {
      document.body.classList.add("rtl");
      document.body.classList.remove("ltr");
    } else {
      document.body.classList.add("ltr");
      document.body.classList.remove("rtl");
    }
  }

  componentDidMount() {
    const {
      settings: { locale, hotelConfig },
      auth: { webPushError, },
      hotelId,
      appPushTokenSuccess,
      setDeviceStatus,
      setMessageHandlerStatus,
      clearSubscribedTopics,
      setRequestLoading,
      acquirePolicies
    } = this.props;

    setRequestLoading(true);

    if (webPushError) {
      this.onShowNotificationPermissionError();
    }

    if (hotelId) {
      sendMessageToWebview(`HotelId:${hotelId}`);
    }

    if (localStorage.getItem("staff-app-push-token")) {
      appPushTokenSuccess(localStorage.getItem("staff-app-push-token"));
    }

    setDeviceStatus("closed");
    setMessageHandlerStatus(false);
    clearSubscribedTopics();
    acquirePolicies();
    this.iotTimer = setInterval(() => { acquirePolicies(); }, 29 * 60 * 1000);

    this.connectionTimer = setTimeout(() => {
      this.setState({ thirtySecondsPassedAfterDidMount: true });
    }, 30000);

    AppLocale[locale] = Dictionary(locale);
    if (hotelConfig.dictionary) {
      AppLocale[locale] = Dictionary(locale, hotelConfig.dictionary);
    }
  }

  async componentDidUpdate(prevProps) {
    const {
      settings: { locale, hotelConfig },
      auth: { userPrivileges, webPushError },
      iot: {
        connectPolicy, publicPublishPolicy, publicReceivePolicy, acquiredPolicies, internetConnected, deviceStatus
      },
      requestApp,
      bookingApp,
      acquirePolicies,
      webPushPermissionStart,
    } = this.props;

    const { settings: prevSettings, iot: prevIot } = prevProps;

    if (prevSettings.locale !== locale) {
      I18n.setLanguage(locale);
      AppLocale[locale] = Dictionary(locale);
      if (hotelConfig.dictionary) {
        AppLocale[locale] = Dictionary(locale, hotelConfig.dictionary);
      }
    }

    if (prevIot.internetConnected === false && internetConnected) {
      acquirePolicies();
    }

    if (JSON.stringify(prevIot.connectPolicy) !== JSON.stringify(connectPolicy)
      || JSON.stringify(prevIot.publicPublishPolicy) !== JSON.stringify(publicPublishPolicy)
      || JSON.stringify(prevIot.publicReceivePolicy) !== JSON.stringify(publicReceivePolicy)
      || JSON.stringify(prevIot.acquiredPolicies) !== JSON.stringify(acquiredPolicies)
      // || prevProps.publicSubscribePolicy !== publicSubscribePolicy
    ) {
      acquirePolicies();
    }

    if (prevIot.deviceStatus !== "none"
      && prevIot.deviceStatus !== "connected"
      && deviceStatus === "connected"
      && this.state.thirtySecondsPassedAfterDidMount
    ) {
      this.onShowConnectedAlert();
    }

    if (prevIot.deviceStatus !== "reconnecting"
      && deviceStatus === "reconnecting"
      && !this.state.reconnectTimer
    ) {
      this.setState({ reconnectTimer: 5 });
      this.startCountDownForReconnection();
    }

    if (prevProps.requestApp.error !== requestApp.error) {
      this.onShowRequestError();
    }

    if (prevProps.bookingApp.error !== bookingApp.error) {
      this.onShowBookingError();
    }

    if ("Notification" in window
      && webPushPermissionRetryCount < MAX_WEBPUSH_PERMISSION_RETRY_COUNT
      && prevProps.webPushError !== webPushError
      && webPushError
    ) {
      webPushPermissionRetryCount++;
      webPushPermissionStart(hotelConfig, userPrivileges);
    }
  }

  componentWillUnmount() {
    clearInterval(this.iotTimer);
    clearInterval(this.timer);
    clearInterval(this.connectionTimer);
    Hub.remove("IOTChannel", this.iotHubListener);
  }

  onIotEvent(payload) {
    const {
      iot: { internetConnected },
      iotConnectCallbackStart,
      iotCloseCallbackStart,
      setDeviceStatus,
      clearSubscribedTopics,
      clearPolicies,
      setMessageHandlerStatus,
      acquirePolicies,
      triggerOnNewMessage
    } = this.props;

    const { event } = payload;
    switch (event) {
      case "connected":
        const { connack } = payload.data;
        clearInterval(this.timer);
        iotConnectCallbackStart({ connack });
        policyRetryCount = 0;
        break;
      case "closed":
        iotCloseCallbackStart();
        break;
      case "error":
        setDeviceStatus("error");
        if (internetConnected && policyRetryCount < MAX_POLICY_RETRY_COUNT) {
          clearSubscribedTopics();
          clearPolicies();
          setMessageHandlerStatus(false);
          acquirePolicies(true);
          policyRetryCount++;
        } else if (policyRetryCount >= MAX_POLICY_RETRY_COUNT) {
          iotCloseCallbackStart();
        }
        break;
      case "reconnecting":
        setDeviceStatus("reconnecting");
        break;
      case "offline":
        setDeviceStatus("offline");
        break;
      case "messageReceived":
        triggerOnNewMessage(payload.data);
        break;
      default:
    }
  }

  onShowRequestError = () => {
    window.setTimeout(() => {
      this.props.setRequestShowToast(false);
    }, 3000);
  };

  onShowNotificationPermissionError = () => {
    this.setState({ notificationPermissionErrorVisible: true }, () => {
      window.setTimeout(() => {
        this.setState({ notificationPermissionErrorVisible: false });
      }, 10000);
    });
  };

  onShowBookingError = () => {
    window.setTimeout(() => {
      this.props.setBookingShowToast(false);
    }, 3000);
  };

  onShowConnectedAlert = () => {
    this.setState({ visibleConnected: true }, () => {
      window.setTimeout(() => {
        this.setState({ visibleConnected: false });
      }, 3000);
    });
  };

  startCountDownForReconnection = () => {
    this.timer = setInterval(() => {
      this.setState({ reconnectTimer: this.state.reconnectTimer - 1 >= 0 ? this.state.reconnectTimer - 1 : 5 });
    }, 3000);
  };

  reloadPage = () => {
    window.location.reload();
  };

  handleInternetStatus = ({ online }) => {
    const {
      iot: { internetConnected, deviceStatus },
      internetConnectedStatusChanged,
    } = this.props;

    if (online !== internetConnected) {
      internetConnectedStatusChanged(online);
    }
    if (online && deviceStatus === "connected") return <div />;
    if ((this.state.thirtySecondsPassedAfterDidMount || !internetConnected) && deviceStatus !== "error") {
      return (
        <Alert color="warning" className="alert-heading fixed-top" style={{ zIndex: 99999, position: "fixed" }} onClick={this.reloadPage}>
          <IntlMessages id="You are currently offline" />
        </Alert>
      );
    }
    return <div />;
  };

  render() {
    const {
      settings: { locale, hotelConfig },
      iot: { deviceStatus },
      requestApp: { error: requestError, showToast: requestShowToast },
      bookingApp: { error: bookingError, showToast: bookingShowToast },
    } = this.props;
    const { visibleConnected, reconnectTimer } = this.state;

    let currentAppLocale = AppLocale[locale];
    if (!currentAppLocale) {
      AppLocale.en = Dictionary("en");
      if (hotelConfig.dictionary) {
        AppLocale.en = Dictionary("en", hotelConfig.dictionary);
      }
      currentAppLocale = AppLocale.en;
    }

    return (
      <div className="h-100">
        <IntlProvider locale={currentAppLocale.locale} messages={currentAppLocale.messages}>
          <React.Fragment>
            {requestError && requestError.message && requestShowToast && (
              <Alert color="danger" className="alert-heading fixed-top" style={{ zIndex: 999999999, position: "fixed" }}>
                <IntlMessages id="Please check your internet connection" />
              </Alert>
            )}
            {bookingError && bookingError.message && bookingShowToast && (
              <Alert className="alert-heading fixed-top" style={{ zIndex: 999999999, position: "fixed" }} color="danger">
                <IntlMessages id="Please check your internet connection" />
              </Alert>
            )}
            <NotificationContainer />
            <VersionChecker />
            {isMultiColorActive && <ColorSwitcher />}
            <Detector render={this.handleInternetStatus} polling={{ url: "/offline.html", interval: 30000 }} />
            {visibleConnected ? (
              <Alert color="success" className="alert-heading fixed-top" isOpen={visibleConnected} style={{ zIndex: 99999, position: "fixed" }}>
                <IntlMessages id="connected_to_the_server" />
              </Alert>
            ) : (
              ""
            )}
            {(deviceStatus === "reconnecting" || deviceStatus === "closed") && reconnectTimer ? (
              <Alert color="warning" className="fixed-top" style={{ zIndex: 9999999999, position: "fixed" }} onClick={this.reloadPage}>
                <div style={{ textDecoration: "underline" }}>
                  {" "}
                  <IntlMessages id="Reconnecting" />:{`${reconnectTimer} `} <IntlMessages id="Retry Now" />{" "}
                </div>
              </Alert>
            ) : (
              ""
            )}
            <DebugRouter>
              <Switch>
                <Route path="/app" component={app} />
                <Route path="/" exact component={main} />
                <Redirect from="/login" to="/app" />
                <Route path="/error" exact component={errorView} />
                <Redirect to="/error" />
              </Switch>
            </DebugRouter>
          </React.Fragment>
        </IntlProvider>
      </div>
    );
  }
}

const mapStateToProps = ({ iot, auth, settings, requestApp, bookingApp }) => {
  const { hotelId } = settings && settings.hotelConfig ? settings.hotelConfig : null;
  return {
    iot,
    auth,
    settings,
    requestApp,
    bookingApp,
    hotelId,
  };
};
const mapActionsToProps = {
  setRequestLoading,
  setDeviceStatus,
  internetConnectedStatusChanged,
  acquirePolicies,
  clearSubscribedTopics,
  setMessageHandlerStatus,
  iotConnectCallbackStart,
  iotCloseCallbackStart,
  triggerOnNewMessage,
  clearPolicies,
  setRequestShowToast,
  setBookingShowToast,
  webPushPermissionStart,
  appPushTokenSuccess
};

export default connect(
  mapStateToProps,
  mapActionsToProps
)(App);
